import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Event } from "./EventService";
import { Room } from "./RoomService";

// GraphQL queries
const EVENT_ROOM_LIST_QUERY = gql`
  query EventRoomList($filter: EventRoomFilter, $params: PaginationAndSorting) {
    eventRoomList(filter: $filter, params: $params) {
      code
      data {
        id
        eventId
        roomId
        room {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_GET_QUERY = gql`
  query EventRoomGet($id: ID) {
    eventRoomGet(id: $id) {
      code
      data {
        id
        eventId
        roomId
        room {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_GET_MANY_QUERY = gql`
  query EventRoomGetMany($ids: [ID]) {
    eventRoomGetMany(ids: $ids) {
      code
      data {
        id
        eventId
        roomId
        room {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EVENT_ROOM_CREATE_MUTATION = gql`
  mutation EventRoomCreate($params: EventRoomInput) {
    eventRoomCreate(params: $params) {
      code
      data {
        id
        eventId
        roomId
        room {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_UPDATE_MUTATION = gql`
  mutation EventRoomUpdate(
    $where: EventRoomWhereInput
    $params: EventRoomInput
  ) {
    eventRoomUpdate(where: $where, params: $params) {
      code
      data {
        id
        eventId
        roomId
        room {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_REMOVE_MUTATION = gql`
  mutation EventRoomRemove($where: EventRoomWhereInput) {
    eventRoomRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_REMOVE_MANY_MUTATION = gql`
  mutation EventRoomRemoveMany($where: [EventRoomWhereInput]) {
    eventRoomRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface EventRoom {
  id: string;
  eventId?: string;
  roomId?: string;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  event?: Event;
  room?: Room;
}

export interface EventRoomFilter {
  id?: string[];
  eventId?: string[];
  roomId?: string[];
}

export interface EventRoomInput {
  eventId?: string;
  roomId?: string;
}

export interface EventRoomWhereInput {
  id: string;
}

/**
 * Event Room Assignment service that handles all event room assignment-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class EventRoomService {
  /**
   * Get a list of event room assignments with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EventRoomFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_ROOM_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single event room assignment by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_ROOM_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple event room assignments by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_ROOM_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new event room assignment
   */
  static async create(params: EventRoomInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_ROOM_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing event room assignment
   */
  static async update(id: string, params: EventRoomInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_ROOM_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an event room assignment
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_ROOM_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple event room assignments
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EVENT_ROOM_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
